/** Prefix of the ID of a number range, e.g. "range:2_10", "range:_10" or "range:2_" */
const NUMBER_RANGE_FILTER_ID_PREFIX = "range:";

/** Separates the minimum and the maximum in the ID of a number range */
const NUMBER_RANGE_SEPARATOR = "_";

/** A limit, which is a whole number that is not negative, in the ID of a number range */
const NUMBER_LIMIT_PATTERN = /^\d+$/;

/** The limits of a number range, of which at least one is set. Both are included */

/** Returns the number of an entity that is filtered by a range, e.g. the projects of a group */

/**
 * Returns the filter options for the number ranges among the IDs, which match the entities whose
 * number is within both limits. The IDs that are no valid number range are left out.
 */
export function getNumberRangeFilterOptions(filterIds, getEntityNumber) {
  return Object.fromEntries(filterIds.flatMap(filterId => {
    const range = parseNumberRangeFilterId(filterId);
    if (!range) {
      return [];
    }
    const {
      min = 0,
      max = Infinity
    } = range;
    return [[filterId, {
      id: filterId,
      label: formatNumberRange(range),
      hasEntityPassedFilter: entity => {
        const value = getEntityNumber(entity);
        return value >= min && value <= max;
      }
    }]];
  }));
}

/** Returns the ID of the filter for the number range */
export function getNumberRangeFilterId({
  min,
  max
}) {
  return `${NUMBER_RANGE_FILTER_ID_PREFIX}${min ?? ""}${NUMBER_RANGE_SEPARATOR}${max ?? ""}`;
}

/**
 * Returns the number range of the ID, or undefined if it is none or invalid: it has no limit, a
 * limit is no whole number that is not negative, or its minimum is larger than its maximum
 */
export function parseNumberRangeFilterId(filterId) {
  if (!filterId.startsWith(NUMBER_RANGE_FILTER_ID_PREFIX)) {
    return undefined;
  }
  const limits = filterId.slice(NUMBER_RANGE_FILTER_ID_PREFIX.length).split(NUMBER_RANGE_SEPARATOR);
  if (limits.length !== 2) {
    return undefined;
  }
  const [min, max] = limits.map(parseNumberLimit);
  if (min === null || max === null || min === undefined && max === undefined) {
    return undefined;
  }
  if (min !== undefined && max !== undefined && min > max) {
    return undefined;
  }
  return {
    min,
    max
  };
}

/** Returns the text that tells which numbers a range matches, e.g. "2 - 10" */
export function formatNumberRange({
  min,
  max
}) {
  if (min !== undefined && max !== undefined) {
    return `${min} - ${max}`;
  }
  return min === undefined ? `Up to ${max}` : `At least ${min}`;
}

/**
 * Returns the limit written in a filter ID, undefined if there is none, or null if it is invalid
 */
function parseNumberLimit(text) {
  if (text === "") {
    return undefined;
  }
  return NUMBER_LIMIT_PATTERN.test(text) ? Number(text) : null;
}